import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { Send, ArrowLeft, Brain, Users, Hash, Shield, Swords, Zap } from 'lucide-react';
import { api, timeAgo } from '../lib/api';
import { useAuth } from '../lib/store';
import { getSocket } from '../lib/socket';
import { ARENA, themeOf } from '../lib/classTheme';
import { Avatar, SkeletonList, Empty } from '../components/ui';

function TriviaLivePanel({ live, remainMs, onAnswer }) {
  if (!live) return null;
  const letters = ['A', 'B', 'C', 'D'];
  const pct = live.seconds
    ? Math.max(0, Math.min(100, (remainMs / (live.seconds * 1000)) * 100))
    : 0;

  return (
    <div className="mx-3 mb-2 rounded-2xl border border-violet-500/30 bg-zinc-900/95 backdrop-blur px-4 py-3 text-white shadow-lg">
      {live.phase === 'countdown' && (
        <div className="text-center text-sm text-violet-300 animate-pulse">
          🧠 Trivia starting… get ready!
        </div>
      )}

      {live.phase === 'question' && (
        <>
          <div className="mb-2 flex items-center justify-between text-xs text-white/60">
            <span>Question {live.index + 1}/{live.total}</span>
            <span className="font-mono">{Math.ceil(remainMs / 1000)}s</span>
          </div>

          <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-violet-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>

          <div className="mb-3 text-sm font-medium text-white">{live.question}</div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(live.options || []).map((opt, idx) => {
              const chosen = live.myAnswer === idx;
              const disabled = live.myAnswer != null;
              return (
                <button
                  key={idx}
                  disabled={disabled}
                  onClick={() => onAnswer(idx)}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition
                    ${chosen
                      ? 'border-violet-400 bg-violet-500/30 text-white'
                      : disabled
                        ? 'border-white/10 bg-white/5 text-white/40'
                        : 'border-white/15 bg-white/5 text-white/90 hover:border-violet-400/60 hover:bg-violet-500/10'}`}
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/10 text-xs font-bold">
                    {letters[idx]}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>

          {live.myAnswer != null && (
            <div className="mt-2 text-xs text-emerald-400">
              ✓ Answer locked in — waiting for others…
            </div>
          )}
          {live.answeredCount > 0 && (
            <div className="mt-1 text-xs text-white/40">
              {live.answeredCount} answered so far
            </div>
          )}
        </>
      )}

      {live.phase === 'reveal' && (
        <>
          <div className="mb-2 text-sm font-semibold text-emerald-400">
            ✅ Correct: {letters[live.correctIndex]} — {live.options?.[live.correctIndex]}
          </div>
          {live.explanation && (
            <div className="mb-2 text-xs text-white/60">💡 {live.explanation}</div>
          )}
          {!!live.leaderboard?.length && (
            <div className="space-y-1">
              {live.leaderboard.slice(0, 5).map((r, i) => (
                <div key={i} className="flex justify-between text-xs text-white/70">
                  <span>{i + 1}. {r.username || r.name}</span>
                  <span className="text-violet-300">{r.points} pts</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {live.phase === 'finished' && (
        <>
          <div className="mb-2 text-sm font-semibold text-white">🏆 Final results</div>
          <div className="space-y-1">
            {(live.finalResults || []).slice(0, 3).map((r, i) => (
              <div key={i} className="flex justify-between text-xs text-white/70">
                <span>{['🥇', '🥈', '🥉'][i]} {r.username}</span>
                <span>{r.correct}/{live.total} · +{r.total} pts</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function ChatRoom() {
  const { roomType, roomId } = useParams();
  const userId = useAuth(s => s.user?.id);
  const username = useAuth(s => s.user?.username);

  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState('');
  const [online, setOnline] = useState(0);
  const [typing, setTyping] = useState([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(null);
  const [remainMs, setRemainMs] = useState(0);

  const scrollRef = useRef();
  const joinedRef = useRef(false);
  const typingTimer = useRef();

  /* ---- room meta (cached) ---- */
  const { data: room } = useQuery({
    queryKey: ['chatRoomMeta', roomType, roomId],
    queryFn: () => {
      if (roomType === 'group') return api.get(`/groups/${roomId}`).then(r => r.data);
      if (roomType === 'guild') return api.get(`/guilds/${roomId}`).then(r => r.data);
      return null;
    },
    enabled: roomType === 'group' || roomType === 'guild',
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  /* ---- history, once per room ---- */
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setMessages([]);
    joinedRef.current = false;

    api.get(`/chat/${roomType}/${roomId}`)
      .then(r => { if (alive) setMessages(r.data || []); })
      .catch(e => { if (alive) toast.error(e.message); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [roomType, roomId]);

  /* ---- socket, once per room ---- */
  useEffect(() => {
    const s = getSocket();
    if (!s || joinedRef.current) return;

    joinedRef.current = true;
    const roomKey = `${roomType}:${roomId}`;
    s.emit('join', roomKey);

    const onMsg = (m) => {
      if (m.room_type && m.room_type !== roomType) return;
      if (m.room_id && String(m.room_id) !== String(roomId)) return;
      setMessages(v => [...v, m]);
    };
    const onPresence = (d) => setOnline(d.count || 0);
    const onTyping = ({ username: u, typing: t }) => {
      if (u === username) return;
      setTyping(v => t ? [...new Set([...v, u])] : v.filter(x => x !== u));
    };
    const onTriviaStarted = (d) => {
	  setLive({ phase: 'countdown', total: d.total, seconds: d.seconds, index: -1 });
	};
	const onTriviaQuestion = (d) => {
	  setLive(v => ({
		...(v || {}),
		phase: 'question',
		index: d.index, total: d.total, seconds: d.seconds,
		question: d.question, options: d.options, category: d.category,
		endsAt: d.endsAt, myAnswer: null, correctIndex: null,
		explanation: null, leaderboard: [], answeredCount: 0,
	  }));
	};
	const onTriviaAnswered = (d) => {
	  setLive(v => v ? { ...v, answeredCount: d.count } : v);
	};
	const onTriviaReveal = (d) => {
	  setLive(v => v ? {
		...v, phase: 'reveal',
		correctIndex: d.correct_index, explanation: d.explanation,
		leaderboard: d.leaderboard,
	  } : v);
	};
	const onTriviaFinished = (d) => {
	  setLive(v => ({ ...(v || {}), phase: 'finished', finalResults: d.results, total: d.total }));
	  setTimeout(() => setLive(v => (v?.phase === 'finished' ? null : v)), 12000);
	};
	const onTriviaCancelled = () => setLive(null);

    s.on('chat:message', onMsg);
	s.on('chat:presence', onPresence);
	s.on('chat:typing', onTyping);
	s.on('trivia:live:started', onTriviaStarted);
	s.on('trivia:live:question', onTriviaQuestion);
	s.on('trivia:live:answered', onTriviaAnswered);
	s.on('trivia:live:reveal', onTriviaReveal);
	s.on('trivia:live:finished', onTriviaFinished);
	s.on('trivia:live:cancelled', onTriviaCancelled);

    return () => {
      s.off('chat:message', onMsg);
	  s.off('chat:presence', onPresence);
	  s.off('chat:typing', onTyping);
	  s.off('trivia:live:started', onTriviaStarted);
	  s.off('trivia:live:question', onTriviaQuestion);
	  s.off('trivia:live:answered', onTriviaAnswered);
	  s.off('trivia:live:reveal', onTriviaReveal);
	  s.off('trivia:live:finished', onTriviaFinished);
	  s.off('trivia:live:cancelled', onTriviaCancelled);
      s.emit('leave', roomKey);
      joinedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomType, roomId]);

  /* ---- autoscroll ---- */
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 9e9, behavior: 'smooth' });
  }, [messages.length]);
  /* ---- trivia countdown ticking ---- */
	useEffect(() => {
	  if (!live || live.phase !== 'question' || !live.endsAt) return;
	  const tick = () => setRemainMs(Math.max(0, live.endsAt - Date.now()));
	  tick();
	  const id = setInterval(tick, 200);
	  return () => clearInterval(id);
	}, [live?.endsAt, live?.phase]);

  function send() {
    const s = getSocket();
    const body = msg.trim();
    if (!s || !body) return;
    s.emit('chat:send', { roomType, roomId, body });
    setMsg('');
    s.emit('chat:typing', { roomType, roomId, typing: false });
    clearTimeout(typingTimer.current);
  }

  function onType(e) {
    setMsg(e.target.value);
    const s = getSocket();
    if (!s) return;
    s.emit('chat:typing', { roomType, roomId, typing: true });
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(
      () => s.emit('chat:typing', { roomType, roomId, typing: false }), 2000);
  }

  function startTrivia() {
    const s = getSocket();
    if (!s || roomType !== 'group') return;
    s.emit('trivia:live:start', { groupId: Number(roomId) }, (r) => {
      if (r?.error) toast.error(r.error);
      else toast.success('Trivia starting! 🧠');
    });
  }
  function answerTrivia(idx) {
	  const s = getSocket();
	  if (!s || !live || live.phase !== 'question' || live.myAnswer != null) return;
	  setLive(v => ({ ...v, myAnswer: idx }));
	  s.emit('trivia:live:answer', { groupId: Number(roomId), index: idx }, (r) => {
		if (r?.error) {
		  toast.error(r.error);
		  setLive(v => (v ? { ...v, myAnswer: null } : v));
		}
	  });
   }


  const Icon = roomType === 'guild' ? Shield
    : roomType === 'fight' ? Swords
    : roomType === 'race' ? Zap : Hash;

  const title = room?.handle || room?.name ||
    (roomType === 'fight' ? 'Duel chat'
      : roomType === 'race' ? 'Race chat'
      : `${roomType} #${roomId}`);

  return (
    <div className="max-w-2xl mx-auto flex flex-col animate-fade-up"
      style={{ height: 'calc(100vh - 7.5rem)' }}>

      {/* ═══ HEADER ═══ */}
      <div className="card-arena p-3 flex items-center gap-2.5 shrink-0">
        <Link to="/chat" className="p-1.5 rounded-lg shrink-0"
          style={{ color: ARENA.arenaText }}>
          <ArrowLeft size={18} />
        </Link>

        <div className="w-9 h-9 grid place-items-center shrink-0"
          style={{
            borderRadius: 'var(--radius-sm)',
            background: 'var(--accent-soft)',
            border: `1px solid ${ARENA.gold}44`,
          }}>
          <Icon size={16} style={{ color: ARENA.arenaText }} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-sm truncate"
            style={{ color: ARENA.arenaText }}>{title}</p>
          <p className="text-[10px] font-semibold truncate" style={{ color: ARENA.arenaTextSoft }}>
            {online > 0 && `${online} online`}
            {typing.length > 0 && `${online > 0 ? ' · ' : ''}${typing.slice(0, 2).join(', ')} typing…`}
            {!online && !typing.length && 'Live chat'}
          </p>
        </div>

        {roomType === 'group' && (
          <button onClick={startTrivia} className="btn-ghost btn-sm shrink-0">
            <Brain size={12} /> Trivia
          </button>
        )}
      </div>

	  <TriviaLivePanel live={live} remainMs={remainMs} onAnswer={answerTrivia} />
      {/* ═══ MESSAGES ═══ */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto py-3 space-y-1.5 min-h-0 px-0.5">
        {loading ? <SkeletonList n={5} h="h-12" />
          : !messages.length
            ? <Empty emoji="💬" title="No messages yet" sub="Be the first to say something." />
            : (
              <AnimatePresence initial={false}>
                {messages.map((m, i) => {
                  if (m.system) {
                    return (
                      <motion.div key={m.id || i}
                        initial={{ opacity: 0, scale: .95 }} animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-1">
                        <span className={m.kind === 'trivia' ? 'chip-grape' : 'chip-ink'}>
                          {m.body}
                        </span>
                      </motion.div>
                    );
                  }

                  const mine = m.user_id === userId;
                  const prev = messages[i - 1];
                  const grouped = prev && !prev.system && prev.user_id === m.user_id &&
                    (new Date(m.created_at) - new Date(prev.created_at)) < 120000;
                  const t = themeOf(m.class_key);

                  return (
                    <motion.div key={m.id || i}
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      className={`flex gap-2 ${mine ? 'flex-row-reverse' : ''}
                        ${grouped ? 'mt-0.5' : 'mt-2.5'}`}>

                      <div className="w-8 shrink-0">
                        {!grouped && (
                          <Link to={`/u/${m.username}`}>
                            <Avatar src={m.image_url || m.avatar_url} name={m.username}
                              size={32} square gold />
                          </Link>
                        )}
                      </div>

                      <div className={`max-w-[76%] ${mine ? 'items-end' : ''} flex flex-col`}>
                        {!grouped && (
                          <div className={`flex items-center gap-1.5 mb-0.5 px-1
                            ${mine ? 'flex-row-reverse' : ''}`}>
                            <span className="font-display font-bold text-[11px] truncate">
                              {m.hero_name || m.display_name || m.username}
                            </span>
                            {m.guild_tag && (
                              <span className="chip-grape !text-[7px] !px-1">{m.guild_tag}</span>
                            )}
                            {m.level && (
                              <span className="text-[9px] font-bold"
                                style={{ color: 'var(--text-muted)' }}>L{m.level}</span>
                            )}
                            <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>
                              {timeAgo(m.created_at)}
                            </span>
                          </div>
                        )}

                        <div className="px-3 py-2 text-sm break-words"
                          style={{
                            borderRadius: 'var(--radius)',
                            background: mine
                              ? `linear-gradient(135deg, ${t.hex}22, ${t.hex}12)`
                              : 'var(--surface)',
                            border: `1px solid ${mine ? t.hex + '44' : 'var(--border)'}`,
                            color: 'var(--text)',
                          }}>
                          {m.body}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
      </div>

      {/* ═══ COMPOSER ═══ */}
      <div className="card p-2 flex gap-2 shrink-0">
        <input className="input !py-2.5 flex-1" placeholder="Type a message…"
          value={msg} maxLength={1000} onChange={onType}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())} />
        <button onClick={send} disabled={!msg.trim()} className="btn-primary !px-4">
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}